import ErrorMessage from 'Components/ErrorMessage'
import LoadingSpinner from 'Components/LoadingSpinner'
import useStores from 'hooks/useStores'
import useTitle from 'hooks/useTitle'
import React, { useEffect, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Service from 'Service'
import { RegionalSettings, TimeZoneOption } from 'Service/Settings'
import { formatCivilDate, formatUtcDateTime, toApiCivilDate } from 'Utilities/dateTime'
import * as yup from 'yup';

const Settings = () => {
    const { t } = useTranslation()
    useTitle(t('settings'))
    const { companyStore, uiStore, regionalSettingsStore } = useStores()
    const [isLoading, setIsLoading] = useState(false)
    const [regionalLoading, setRegionalLoading] = useState(true)
    const [regionalSaving, setRegionalSaving] = useState(false)
    const [timeZones, setTimeZones] = useState<TimeZoneOption[]>([])
    const [regional, setRegional] = useState<RegionalSettings>({
        DateFormat: companyStore.selectedCompany?.Settings?.DateFormat || 'dd/MM/yyyy',
        TimeZoneId: companyStore.selectedCompany?.Settings?.TimeZoneId || 'America/Caracas'
    })

    const validationSchema = yup.object().shape({
        Correlative: yup.number().required(),
    });

    const { register, handleSubmit, errors } = useForm({ validationSchema: validationSchema });

    useEffect(() => {
        Promise.all([Service.Settings.getRegional(), Service.Settings.getTimeZones()])
            .then(([settings, zones]) => {
                setRegional(settings)
                setTimeZones(zones)
                regionalSettingsStore.setPreferences(settings)
            })
            .catch(() => {
                setTimeZones([
                    { Id: 'America/Caracas', DisplayName: 'Caracas', BaseUtcOffset: '-04:00:00' },
                    { Id: 'UTC', DisplayName: 'UTC', BaseUtcOffset: '00:00:00' }
                ])
            })
            .finally(() => setRegionalLoading(false))
    }, [regionalSettingsStore])

    function onSubmit(data: any) {
        setIsLoading(true)
        Service.Settings.changeCorrelativeWithholding(data.Correlative).then((res) => {
            companyStore.updateCompanyCurrentCorrelative(data.Correlative)
            uiStore.addNotification(uiStore.appNotifications.SUCCESS_SETTINGS_CHANGED)
        }).catch((err) => {
            if (err.response?.status === 400) {
                uiStore.addNotification(uiStore.appNotifications.INVALID_CORRELATIVE)
                return
            }
            if (err.response?.status === 404) {
                throw new Error("Invalid correlative change attempt: company not found");
            }
            throw err
        }).finally(() => setIsLoading(false))
    }

    const saveRegional = (event: React.FormEvent) => {
        event.preventDefault()
        setRegionalSaving(true)
        Service.Settings.updateRegional(regional)
            .then(settings => {
                setRegional(settings)
                companyStore.updateRegionalSettings(settings)
                uiStore.addNotification(uiStore.appNotifications.SUCCESS_SETTINGS_CHANGED)
            })
            .catch(() => uiStore.addNotification(uiStore.appNotifications.INTERNAL_SERVER_ERROR))
            .finally(() => setRegionalSaving(false))
    }

    const previewCivil = toApiCivilDate(new Date())
    const previewUtc = new Date()

    return <div className="pb-4">
        <div className="mb-4">
            <h1 className="h3 mb-1">{t('settings')}</h1>
            <p className="text-muted mb-0">
                {t('general_settings_hint', { defaultValue: 'Preferencias operativas de la empresa seleccionada.' })}
            </p>
        </div>

        <Row>
            <Col xs={12} lg={7} className="mb-3">
                <Card className="h-100 shadow-sm border-0">
                    <Card.Body className="p-4">
                        <h2 className="h5 mb-1">
                            {t('regional_settings', { defaultValue: 'Fecha y zona horaria' })}
                        </h2>
                        <p className="text-muted small mb-4">
                            {t('regional_settings_hint', {
                                defaultValue: 'Las horas se almacenan en UTC y se muestran en la zona horaria seleccionada.'
                            })}
                        </p>

                        {regionalLoading ? <LoadingSpinner /> : (
                            <Form onSubmit={saveRegional}>
                                <fieldset disabled={regionalSaving}>
                                    <Form.Group>
                                        <Form.Label>{t('date_format', { defaultValue: 'Formato de fecha' })}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={regional.DateFormat}
                                            onChange={event => setRegional({
                                                ...regional,
                                                DateFormat: event.target.value as RegionalSettings['DateFormat']
                                            })}
                                        >
                                            <option value="dd/MM/yyyy">dd/MM/yyyy</option>
                                            <option value="MM/dd/yyyy">MM/dd/yyyy</option>
                                            <option value="yyyy/MM/dd">yyyy/MM/dd</option>
                                        </Form.Control>
                                    </Form.Group>

                                    <Form.Group>
                                        <Form.Label>{t('time_zone', { defaultValue: 'Zona horaria' })}</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={regional.TimeZoneId}
                                            onChange={event => setRegional({ ...regional, TimeZoneId: event.target.value })}
                                        >
                                            {!timeZones.some(zone => zone.Id === regional.TimeZoneId) && (
                                                <option value={regional.TimeZoneId}>{regional.TimeZoneId}</option>
                                            )}
                                            {timeZones.map(zone => (
                                                <option key={zone.Id} value={zone.Id}>
                                                    {zone.Id} — {zone.DisplayName}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    </Form.Group>

                                    <div className="rounded bg-light p-3 mb-3" aria-live="polite">
                                        <span className="text-muted small d-block">
                                            {t('format_preview', { defaultValue: 'Vista previa' })}
                                        </span>
                                        <strong className="d-block">
                                            {formatCivilDate(previewCivil, regional.DateFormat)}
                                        </strong>
                                        <span className="small">
                                            {formatUtcDateTime(previewUtc, {
                                                dateFormat: regional.DateFormat,
                                                timeZoneId: regional.TimeZoneId
                                            })}
                                        </span>
                                    </div>

                                    <Button type="submit" disabled={regionalSaving}>
                                        {regionalSaving
                                            ? t('saving', { defaultValue: 'Guardando...' })
                                            : t('save')}
                                    </Button>
                                </fieldset>
                            </Form>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            <Col xs={12} lg={5} className="mb-3">
                <Card className="h-100 shadow-sm border-0">
                    <Card.Body className="p-4">
                        <h2 className="h5 mb-3">
                            {t('withholding_settings', { defaultValue: 'Retenciones' })}
                        </h2>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <fieldset disabled={isLoading}>
                                <Form.Group>
                                    <Form.Label>{t('correlative')}</Form.Label>
                                    <Form.Control type="number"
                                        defaultValue={companyStore.selectedCompany.Settings.WithholdingCorrelative}
                                        ref={register}
                                        name="Correlative"
                                    />
                                    <ErrorMessage error={errors['Correlative']} />
                                </Form.Group>
                                <Button type="submit">
                                    {t('save')}
                                </Button>
                                {isLoading && <LoadingSpinner/>}
                            </fieldset>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </div>
}

export default Settings